import { AlertTriangle, Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { formatBookingSchedule } from "@/lib/format";
import type { Booking } from "@/types/doctor";

/**
 * Cancelling is irreversible from this portal — there is no "un-cancel"
 * endpoint — so it always goes through this confirmation.
 */
export function CancelBookingDialog({
  booking,
  open,
  onOpenChange,
  onConfirm,
  pending,
}: {
  booking: Booking | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
  pending: boolean;
}) {
  if (!booking) return null;

  const isVideo = booking.consultationType === "video";

  return (
    <Dialog open={open} onOpenChange={pending ? () => {} : onOpenChange}>
      <DialogContent className="rounded-2xl sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <AlertTriangle className="size-5 text-destructive" aria-hidden="true" />
            Cancel this appointment?
          </DialogTitle>
          <DialogDescription>
            {booking.patientName} · {formatBookingSchedule(booking)}
          </DialogDescription>
        </DialogHeader>

        <div className="rounded-xl border border-destructive/25 bg-destructive/8 p-3 text-xs leading-relaxed text-destructive">
          The appointment moves to <strong>Cancelled</strong> and the patient loses the slot.
          {isVideo ? " The video consultation can no longer be started." : ""} This cannot be undone
          from the portal.
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={pending}>
            Keep appointment
          </Button>
          <Button variant="destructive" onClick={onConfirm} disabled={pending}>
            {pending && <Loader2 className="size-4 animate-spin" aria-hidden="true" />}
            Cancel appointment
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
